import * as Sentry from '@sentry/react';
import { generateXID } from './generate-xid';

interface LogErrorOptions {
	error: unknown;
	context: string;
	level?: 'error' | 'warning';
	extraTags?: Record<string, string>;
}

/**
 * One XID per error object, so re-reporting the same error returns the same code
 * instead of minting a new one.
 *
 * Error screens can render more than once — React may discard a `useMemo` cache,
 * remount the component, or re-render with a new error identity. Without this,
 * each render captured a fresh event with a fresh XID, and Sentry's `Dedupe`
 * integration dropped every event after the first as an identical exception. The
 * user was then shown the last render's code while Sentry only held the first.
 */
const xidByError = new WeakMap<object, string>();

/**
 * Logs an error to Sentry with a generated XID for tracking
 * Returns the XID for display in the UI
 */
export const logErrorToSentry = ({
	error,
	context,
	level = 'error',
	extraTags = {},
}: LogErrorOptions): string => {
	// Primitives thrown as errors cannot be cached, so they keep the old behaviour.
	const errorKey =
		typeof error === 'object' && error !== null ? error : undefined;

	if (errorKey !== undefined) {
		const reportedXid = xidByError.get(errorKey);

		if (reportedXid !== undefined) {
			return reportedXid;
		}
	}

	const xid = generateXID();

	if (errorKey !== undefined) {
		xidByError.set(errorKey, xid);
	}

	Sentry.captureException(error, {
		level,
		tags: {
			context,
			xid,
			...extraTags,
		},
		extra: {
			xid,
			errorMessage: error instanceof Error ? error.message : String(error),
			errorStack: error instanceof Error ? error.stack : undefined,
		},
	});

	return xid;
};
